import ReactGA from 'react-ga'
import {setReactGA} from '@helpers/getReactGA'
import logger from '@helpers/logger'
import isScrapping from '@helpers/misc/isScrapping'
import trackPageView from '@helpers/track/trackPageView'
import useCookiesAccepted from '@hooks/useCookiesAccepted'
import useEffect from '@hooks/useEffect'
import useInitialData from '@page-components/Layout/useInitialData'
import GA4React from 'ga-4-react'
import {useRouter} from 'next/router'

const initGA4 = (codeV4: string, router) => {
  const url = new URL(document.location.href)
  const searchParams = new URLSearchParams(url.search)

  const clientId = searchParams.get('GAClientId')
  const sessionId = searchParams.get('GASessionId')

  const customConfig = {
    ...(clientId && sessionId && {client_id: clientId, session_id: sessionId})
  }

  const ga4react = new GA4React(codeV4, customConfig as any)

  ga4react.initialize().then(
    ga4 => {
      searchParams.delete('GAClientId')
      searchParams.delete('GASessionId')
      url.search = searchParams.toString()

      if (clientId || sessionId) {
        window.history.replaceState({}, '', url.toString())
      }

      ga4.pageview(router.asPath)
    },
    err => {
      logger.warn('GA4React failed to initialize', {error: err})
    }
  )
}

export default function GoogleAnalytics() {
  const {integrations: config} = useInitialData()
  const cookies = useCookiesAccepted()

  const getCode = () => {
    if (isScrapping) return

    if (!config) return
    if (!config.integrations) return
    if (!config.integrations.googleAnalyticsId) return

    return config.integrations.googleAnalyticsId
  }

  const getCodeV4 = () => {
    if (isScrapping) return

    if (!config) return
    if (!config.integrations) return
    if (!config.integrations.googleAnalyticsV4Id) return

    return config.integrations.googleAnalyticsV4Id
  }

  const router = useRouter()

  useEffect(() => {
    const code = getCode()
    const codeV4 = getCodeV4()

    if (!code && !codeV4) return

    if (code) {
      ReactGA.initialize(code)
      ReactGA.plugin.require('ec')
      setReactGA(ReactGA)
    }

    if (!code && codeV4 && !GA4React.isInitialized()) {
      initGA4(codeV4, router)
    }

    trackPageView(router.asPath)
  }, [cookies])

  return null
}
